import React from "react";
import ButtonGroups, { BGItem } from "components/ButtonGroups";
import { useTranslation } from "react-i18next";
import Table from "components/Table";
import { ColumnDef } from "@tanstack/react-table";
import clsx from "clsx";
import { Materials2050EPDDetailOUT } from "api-client";
import { roundNumber } from "utils";

interface GroupCategory {
  id: string;
  name: string;
  values: string[];
}

const ENVIRONMENTAL_CATEGORIES: GroupCategory[] = [
  { id: "1", name: "A1-A3", values: ["A1", "A2", "A3", "A1-A3"] },
  { id: "2", name: "A4-A5", values: ["A4", "A5"] },
  {
    id: "3",
    name: "B1-B7",
    values: ["B1", "B2", "B3", "B4", "B5", "B6", "B7"],
  },
  { id: "4", name: "C1-C4", values: ["C1", "C2", "C3", "C4"] },
  { id: "5", name: "D", values: ["D"] },
];

const EnvironmentalIndicators = ({
  details,
}: {
  details: Materials2050EPDDetailOUT | null;
}) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = React.useState<BGItem>(
    ENVIRONMENTAL_CATEGORIES[0]
  );
  const [columnsList, setColumnsList] = React.useState<string[]>([]);
  const [dataList, setDataList] = React.useState<object[]>([]);

  React.useEffect(() => {
    if (details) {
      onChangeGroups(ENVIRONMENTAL_CATEGORIES[0]);
    }
  }, [details]);

  const onChangeGroups = (item: BGItem | undefined) => {
    if (!item) return;
    setColumnsList([
      "indicator",
      ...(item as GroupCategory).values.map((rec) => rec),
    ]);
    const values = details?.indicators.map((rec) => {
      const row: { [key: string]: string | number | null | undefined } = {
        indicator: rec.indicator_name,
      };
      rec.module_values.forEach((module) => {
        if (module.module_name) {
          row[module.module_name] = roundNumber(Number(module.value));
        }
      });
      return row;
    });
    values?.length && setDataList(values?.flat());
    setSelectedCategory(item);
  };

  const getColumns = () =>
    React.useMemo<ColumnDef<{ [key: string]: string }>[]>(
      () => [
        ...columnsList.map((col) => ({
          id: col,
          header: () => (
            <div className="bg-gray-50 text-gray-900 font-semibold">
              {t(`${col === "indicator" ? t("productEditor.indicator") : col}`)}
            </div>
          ),
          cell: (info: { row: { original: { [key: string]: string } } }) => (
            <div
              className={clsx({
                "text-gray-900 font-semibold": col === "indicator",
              })}
            >
              {info.row.original[col] || "-"}
            </div>
          ),
        })),
      ],
      [columnsList]
    );

  return (
    <div className="mb-4">
      <div className="font-semibold text-base mb-3">
        {t("productEditor.environmentalIndicators")}
      </div>
      <ButtonGroups
        items={ENVIRONMENTAL_CATEGORIES}
        current={selectedCategory}
        onChange={onChangeGroups}
        optionClsssName="!h-6"
        containerClassName="mb-2"
      />
      <div className="border border-gray-300 rounded-md overflow-hidden">
        <Table
          data={dataList}
          getColumns={getColumns as () => ColumnDef<unknown, unknown>[]}
          headerRowStyle="bg-white whitespace-nowrap"
          headerColumnStyle="border-r last-of-type:border-r-0 bg-gray-50"
          headerDivStyle="!py-1"
          customColumnStyle="border-r last-of-type:border-r-0 whitespace-nowrap !py-1 !px-2 first-of-type:bg-gray-50"
          enableTooltip
        />
      </div>
    </div>
  );
};

export default EnvironmentalIndicators;
